/**
 * Reports page — roster of draft / finalized / amended reports
 * across both endoscopy and pathology specialties.
 *
 * The server-side `GET /viewer-api/reports/list` merges both
 * `endo_reports` and `path_reports` into a single response sorted
 * by `updated_at desc`. A logged-in user sees:
 *   · their OWN reports (any status, so drafts are resumable)
 *   · everyone else's finalized / amended reports (read-only)
 *
 * The `Signed PDF + DICOM SR archive` note from the placeholder
 * still holds as a forward-looking milestone — write-path for
 * server-rendered PDFs and DICOM Structured Reports lands in a
 * later sprint. The current action is "Open" which routes the
 * user to the study viewer, where the existing in-shell PDF
 * export (print-to-PDF) works today.
 */
import { createResource, createSignal, For, Show } from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import { auth } from "../auth/store";

type ReportKind = "endoscopy" | "pathology";
type ReportStatus = "draft" | "finalized" | "amended";

interface ReportRow {
  kind:            ReportKind;
  study_uid:       string;
  status:          ReportStatus;
  procedure?:      string;
  user_id:         string;
  user_name?:      string | null;
  patient_name?:   string | null;
  patient_mrn?:    string | null;
  finalized_at?:   string | null;
  finalized_by?:   string | null;
  updated_at:      string;
  label_count:     number;
  snapshot_count?: number;
  is_own:          boolean;
}

interface ReportListResponse {
  reports: ReportRow[];
  viewer_user_id: string;
}

async function fetchReports(): Promise<ReportListResponse> {
  const a = auth();
  const headers: Record<string, string> = a?.accessToken
    ? { Authorization: `Bearer ${a.accessToken}` } : {};
  const r = await fetch("/viewer-api/reports/list", {
    headers: { Accept: "application/json", ...headers },
  });
  if (!r.ok) throw new Error(`reports list: ${r.status}`);
  return r.json();
}

function fmtDate(s?: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isFinite(d.getTime())
    ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "—";
}

export function ReportsPage() {
  const nav = useNavigate();
  const [data] = createResource(fetchReports);

  /** Active filter set. Multiple toggles are OR-ed within a group
   *  and AND-ed across groups. Matches the convention used by the
   *  endoscopy / pathology shells' right-dock tabs. */
  const [statusFilter, setStatusFilter] = createSignal<ReportStatus | "all">("all");
  const [ownerFilter,  setOwnerFilter]  = createSignal<"mine" | "all">("all");
  const [q, setQ] = createSignal("");

  const rows = (): ReportRow[] => {
    const all = data()?.reports ?? [];
    const search = q().trim().toLowerCase();
    return all.filter((r) => {
      if (statusFilter() !== "all" && r.status !== statusFilter()) return false;
      if (ownerFilter()  === "mine" && !r.is_own)                  return false;
      if (search) {
        const blob = [
          r.study_uid, r.patient_name, r.patient_mrn,
          r.user_name, r.procedure, r.finalized_by,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(search)) return false;
      }
      return true;
    });
  };

  const counts = () => {
    const all = data()?.reports ?? [];
    return {
      total:     all.length,
      drafts:    all.filter((r) => r.status === "draft").length,
      finalized: all.filter((r) => r.status === "finalized").length,
      amended:   all.filter((r) => r.status === "amended").length,
      mine:      all.filter((r) => r.is_own).length,
    };
  };

  return (
    <div class="flex h-full flex-col">
      {/* Page header */}
      <div class="border-b border-ink-300 px-6 py-3">
        <div class="flex items-baseline gap-3">
          <h1 class="text-lg font-semibold text-white">Reports</h1>
          <span class="text-[11px] text-ink-700">
            Signed PDF + DICOM SR archive — server-rendered write-path lands in Sprint 5; today's PDF export ships from each specialty shell.
          </span>
        </div>
      </div>

      {/* Filter strip */}
      <div class="flex flex-wrap items-center gap-2 border-b border-ink-300 bg-ink-100 px-6 py-2 text-[11px]">
        <span class="text-[10px] uppercase tracking-wider text-ink-700">status</span>
        <FilterBtn active={statusFilter() === "all"}       onClick={() => setStatusFilter("all")}>All</FilterBtn>
        <FilterBtn active={statusFilter() === "draft"}     onClick={() => setStatusFilter("draft")}>Draft</FilterBtn>
        <FilterBtn active={statusFilter() === "finalized"} onClick={() => setStatusFilter("finalized")}>Finalized</FilterBtn>
        <FilterBtn active={statusFilter() === "amended"}   onClick={() => setStatusFilter("amended")}>Amended</FilterBtn>
        <span class="mx-2 h-4 w-px bg-ink-300" />
        <FilterBtn active={ownerFilter() === "mine"} onClick={() => setOwnerFilter(ownerFilter() === "mine" ? "all" : "mine")}>
          Mine only <span class="ml-1 font-mono text-[10px]">{counts().mine}</span>
        </FilterBtn>
        <input type="text"
               class="ml-auto w-48 rounded border border-ink-300 bg-ink-50 px-2 py-1 text-[11px] text-ink-900 focus:border-brand focus:outline-none"
               placeholder="Search patient, study, MRN…"
               value={q()}
               onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)} />
      </div>

      {/* Body */}
      <div class="min-h-0 flex-1 overflow-auto">
        <Show when={data.loading}>
          <div class="p-6 text-[11px] text-ink-700">Loading reports…</div>
        </Show>
        <Show when={data.error}>
          <div class="p-6 text-[11px] text-danger">
            Failed to load reports: {String(data.error)}
          </div>
        </Show>
        <Show when={data() && !data.loading && rows().length === 0}>
          <div class="p-8 text-center text-[11px] text-ink-700">
            No reports match the current filter.
          </div>
        </Show>
        <Show when={rows().length > 0}>
          <table class="w-full border-collapse text-[11px]">
            <thead class="sticky top-0 z-10 bg-ink-100 text-left text-[10px] uppercase tracking-wider text-ink-700">
              <tr>
                <th class="border-b border-ink-300 px-4 py-2">Status</th>
                <th class="border-b border-ink-300 px-4 py-2">Patient</th>
                <th class="border-b border-ink-300 px-4 py-2">MRN</th>
                <th class="border-b border-ink-300 px-4 py-2">Procedure</th>
                <th class="border-b border-ink-300 px-4 py-2">Reader</th>
                <th class="border-b border-ink-300 px-4 py-2">Updated</th>
                <th class="border-b border-ink-300 px-4 py-2 text-right">Labels</th>
                <th class="border-b border-ink-300 px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <For each={rows()}>{(r) => (
                <tr class="border-b border-ink-300 hover:bg-ink-200"
                    classList={{ "bg-brand/5": r.is_own }}>
                  <td class="px-4 py-2">
                    <span class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                          classList={{
                            "bg-ink-200 text-ink-800 ring-1 ring-ink-300": r.status === "draft",
                            "bg-brand/15 text-brand ring-1 ring-brand":    r.status === "finalized",
                            "bg-warn/15 text-warn ring-1 ring-warn":       r.status === "amended",
                          }}>
                      {r.status}
                    </span>
                  </td>
                  <td class="px-4 py-2 text-ink-900">{r.patient_name ?? "—"}</td>
                  <td class="px-4 py-2 font-mono text-ink-700">{r.patient_mrn ?? "—"}</td>
                  <td class="px-4 py-2 text-ink-900">{r.procedure ?? "—"}</td>
                  <td class="px-4 py-2">
                    <span class="text-ink-900">{r.user_name ?? r.user_id}</span>
                    <Show when={r.is_own}>
                      <span class="ml-1 rounded bg-brand/15 px-1 py-0.5 text-[9px] text-brand">mine</span>
                    </Show>
                  </td>
                  <td class="px-4 py-2 text-ink-700">
                    {fmtDate(r.finalized_at ?? r.updated_at)}
                    <Show when={r.finalized_by}>
                      <div class="text-[9px] text-ink-700">by {r.finalized_by}</div>
                    </Show>
                  </td>
                  <td class="px-4 py-2 text-right font-mono text-ink-900">
                    {r.label_count}
                    <Show when={r.snapshot_count}>
                      <span class="ml-1 text-[9px] text-ink-700">(+{r.snapshot_count} snap)</span>
                    </Show>
                  </td>
                  <td class="px-4 py-2 text-right">
                    <button type="button"
                            class="rounded border border-brand bg-brand/15 px-2 py-0.5 text-[10px] text-brand hover:bg-brand hover:text-white"
                            onClick={() => nav(`/study/${r.study_uid}`)}>
                      open
                    </button>
                  </td>
                </tr>
              )}</For>
            </tbody>
          </table>
        </Show>
      </div>

      {/* Footer note — sprint 5 placeholder */}
      <div class="border-t border-ink-300 bg-ink-100 px-6 py-2 text-[10px] text-ink-700">
        <strong class="text-ink-900">Sprint 5:</strong>
        server-rendered signed PDF (`GET /viewer-api/reports/{"{uid}"}/pdf`)
        + DICOM Structured Report encapsulation (SOP 1.2.840.10008.5.1.4.1.1.88.11)
        + detached digital signature (XAdES). Today's PDF export is
        client-side print from each specialty shell — click <em>open</em>
        on a row to navigate there.
      </div>
    </div>
  );
}

function FilterBtn(p: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button type="button"
            class="rounded border px-2 py-0.5 text-[10px] transition"
            classList={{
              "border-brand bg-brand/15 text-brand": p.active,
              "border-ink-300 bg-ink-50 text-ink-800 hover:text-white": !p.active,
            }}
            onClick={p.onClick}>
      {p.children}
    </button>
  );
}

// Retained so existing router imports remain valid in case any
// test or doc references it.
export default ReportsPage;
